import {IncomingHttpHeaders} from 'http';

import {compare, increment, COMPARE_UNIT, INCREMENT_UNIT} from '@udemy/i18n';

import {
    DateIntervalType,
    type CurrentSubscriptionEnrollmentQuery,
    SubscriptionPlanProductType,
    SubscriptionStatus,
    useCurrentSubscriptionEnrollmentQuery,
} from 'src/generated/graphql';

import {getPassThroughHeaders} from './fetch-utils';

const DAYS_BEFORE_RENEWAL = 30;
const DAYS_AFTER_RENEWAL = 7;

/** Matches {@link fetchSubscriptionEnrollment} — same fields as the CurrentSubscriptionEnrollment operation. */
export type SubscriptionEnrollment = NonNullable<
    CurrentSubscriptionEnrollmentQuery['currentSubscriptionEnrollment']
> | null;

/**
 * Personal Plan product types that should redirect to /personal/home/
 * Includes combined Personal Plan + GwG subscriptions.
 * Excludes GrowWithGoogle-only subscriptions - these stay on marketplace LIHP.
 */
export const PERSONAL_PLAN_PRODUCT_TYPES: SubscriptionPlanProductType[] = [
    SubscriptionPlanProductType.Consumersubscription,
    SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
    SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
];

/**
 * Canceled is technically active since it means they canceled but still have remaining time
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = [
    SubscriptionStatus.Trial,
    SubscriptionStatus.Active,
    SubscriptionStatus.Canceled,
];

/**
 * Fetches current subscription enrollment data from GraphQL.
 * Can be used both client-side (no headers) and server-side (with headers).
 * Returns query structure with null enrollment if request fails.
 */
export const fetchSubscriptionEnrollment = async (
    reqHeaders: IncomingHttpHeaders,
): Promise<SubscriptionEnrollment> => {
    try {
        const headers: Record<string, string> = getPassThroughHeaders(reqHeaders);
        const response = await useCurrentSubscriptionEnrollmentQuery.fetcher({}, headers)();
        return response?.currentSubscriptionEnrollment ?? null;
    } catch (error: unknown) {
        let message = 'unknown';
        let cause = 'unknown';

        if (error instanceof Error) {
            message = error.message;

            const c = error.cause;
            if (c instanceof Error) {
                cause = c.message;
            } else if (typeof c === 'string') {
                cause = c;
            }
        }
        // eslint-disable-next-line no-console
        console.warn('Error fetching subscription enrollment', {message, cause});

        return null;
    }
};

/**
 * Canceled-but-still-active: the user canceled and their subscription
 * expires within the next 30 days.
 */
export function isCanceledNearExpiry(enrollment: SubscriptionEnrollment): boolean {
    if (
        enrollment?.status !== SubscriptionStatus.Canceled ||
        enrollment?.renewalInterval.type !== DateIntervalType.Year
    ) {
        return false;
    }

    const expiryString = enrollment.billing?.currentPeriodEndDate;
    if (!expiryString) return false;

    const now = new Date();
    const expiry = new Date(expiryString);

    if (compare(now, expiry, COMPARE_UNIT.DAY) >= 0) return false;

    const oneMonthFromNow = increment(now, 1, INCREMENT_UNIT.MONTH);
    return compare(expiry, oneMonthFromNow, COMPARE_UNIT.DAY) <= 0;
}

/**
 * Active annual sub near renewal: the user's billing period ends within
 * 30 days or ended up to 7 days ago.
 */
export function isActiveNearRenewal(enrollment: SubscriptionEnrollment): boolean {
    if (
        enrollment?.status !== SubscriptionStatus.Active ||
        enrollment?.renewalInterval.type !== DateIntervalType.Year
    ) {
        return false;
    }

    const renewalString = enrollment.billing?.currentPeriodEndDate;
    if (!renewalString) return false;

    const now = new Date();
    const renewal = new Date(renewalString);

    const startWindow = increment(now, -DAYS_AFTER_RENEWAL, INCREMENT_UNIT.DAY);
    const endWindow = increment(now, DAYS_BEFORE_RENEWAL, INCREMENT_UNIT.DAY);

    return (
        compare(renewal, startWindow, COMPARE_UNIT.DAY) >= 0 &&
        compare(renewal, endWindow, COMPARE_UNIT.DAY) <= 0
    );
}

/**
 * Checks if a user has a Consumer Subscription via GraphQL.
 * Returns true for CONSUMERSUBSCRIPTION.
 *
 * @returns true if user has a Consumer Subscription subscription, false otherwise
 * @param enrollment
 */
export const isConsumerSubscriber = (enrollment: SubscriptionEnrollment): boolean =>
    enrollment?.subscribedPlan?.productType === SubscriptionPlanProductType.Consumersubscription;

export function isEligibleForEngagementRecap(enrollment: SubscriptionEnrollment): boolean {
    return (
        isConsumerSubscriber(enrollment) &&
        (isActiveNearRenewal(enrollment) || isCanceledNearExpiry(enrollment))
    );
}

/**
 * Checks if a user has an active Personal Plan subscription via GraphQL.
 * Returns true for CONSUMERSUBSCRIPTION, CONSUMERSUBSCRIPTION_VALUE_PLAN,
 * and CONSUMERSUBSCRIPTION_PLUS_GROW_WITH_GOOGLE with ACTIVE status.
 * Returns false for GrowWithGoogle-only subscriptions or expired/canceled subscriptions.
 *
 * @returns true if user has an ACTIVE Personal Plan subscription, false otherwise
 * @param reqHeaders
 */
export const isPersonalPlanSubscriber = async (
    reqHeaders: IncomingHttpHeaders,
): Promise<boolean> => {
    const enrollment = await fetchSubscriptionEnrollment(reqHeaders);

    if (!enrollment?.subscribedPlan?.productType) return false;

    return (
        ACTIVE_SUBSCRIPTION_STATUSES.includes(enrollment.status) &&
        PERSONAL_PLAN_PRODUCT_TYPES.includes(enrollment.subscribedPlan.productType)
    );
};
